import { useContext, useRef } from "react"
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from "react-router-dom"
import PropTypes from "prop-types"
import gaTracking from "../../helpers/gaTracking"
import _ from "lodash"

// Context
import { FormContext } from "../../context/formContext"
import { UserContext } from "../../context/userContext"

// Prime
import { Toast } from "primereact/toast"
import { classNames } from "primereact/utils"

import {
  getFormValidationToast,
  getPrefix,
  smoothScrollToTop,
} from "./helpers/FormNavHelpers"
import styles from "../../styles/Nav-Form.module.css"

export default function FormNav({ blocks, templateCategory }) {
  const toast = useRef(null)
  const form = useContext(FormContext)
  const location = useLocation()
  const { idTemplate, formPage, idStamp = null } = useParams()
  const navigate = useNavigate()
  const previousUrl = location.pathname
  const userCtx = useContext(UserContext)

  const getCurrentBlockName = () => {
    return blocks[Number(form.currentPage) - 1]?.block_name || ""
  }

  const isCurrentBlockEdited = () => {
    const currentBlockName = getCurrentBlockName()
    return form.blocksEdited[currentBlockName]
  }

  const listOfBlocks = _.map(blocks, (block) => {
    const { idBlock, block_title, index } = block
    const isActive = Number(formPage) === Number(index)
    const isValid = form.blocksSaved[block.block_name]
    const isEdit = form.blocksEdited[block.block_name]
    const isRequired = _.includes(form.blocksRequired, block.block_name)

    const handlePageJump = async () => {
      // For validation, before saving and navigating
      const valuesFormik = form.formikState?.values
      const isDirtyFormik = form.formikState?.isDirty
      const submitFormik = form.formikState?.submitForm
      const validateFormik = form.formikState?.validateForm

      // Determine whether we should run validation before navigating:
      // - Validate only when the form is dirty
      const shouldValidate = isDirtyFormik || isCurrentBlockEdited()
      if (shouldValidate) {
        // Validate form before navigating
        const errors = await validateFormik()
        if (Object.keys(errors).length > 0) {
          submitFormik() // to trigger error display in UI
          if (toast.current) {
            toast.current.show(
              getFormValidationToast(
                errors,
                form.blocksFetched[getCurrentBlockName()]?.fields,
              ),
            )
          }

          // Stop navigation
          return
        }
      }

      // Determine whether we should save the block:
      // - Save only if the form is dirty (and validation passed)
      const shouldSave = isDirtyFormik || isCurrentBlockEdited()
      if (shouldSave) {
        // Save current block before navigating
        form.handleSaveBlock(getCurrentBlockName(), valuesFormik)
      }

      const nextPage = () => {
        const prefix = getPrefix(idTemplate, previousUrl)
        const pattern = form.editMode
          ? `${prefix}/:formPage/${form.idStamp}`
          : `${prefix}/:formPage/`

        return generatePath(pattern, { formPage: index })
      }

      /*Google Analytics tracking*/
      const gaTrackingParams = {
        event: "template_step",
        userCtx,
        payload: {
          template_name: idTemplate,
          step_modality: "view",
          block_name: block.block_name,
          stamp_id: idStamp || "",
          template_category: templateCategory,
        },
      }
      gaTracking(gaTrackingParams)
      /*End Google Analytics tracking*/

      navigate(nextPage(), { replace: true })

      // Smooth scroll to top of form
      smoothScrollToTop()
    }

    const getStatusClass = () => {
      if (isValid) {
        return styles.navStatusSaved
      } else if (isEdit) {
        return styles.navStatusEdit
      } else if (isRequired) {
        return styles.navStatusRequired
      } else {
        return styles.navStatusDefault
      }
    }

    return (
      <li
        key={idBlock}
        className={classNames(isActive ? styles.active : null)}
        onClick={handlePageJump}
        role="button"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handlePageJump()
        }}
      >
        <div className={classNames(styles.navStatus, getStatusClass())} />
        <div>{block_title}</div>
      </li>
    )
  })

  return (
    <ul className={styles.nav}>
      {listOfBlocks}
      <Toast ref={toast} position="top-right" />
    </ul>
  )
}

FormNav.propTypes = {
  blocks: PropTypes.array.isRequired,
  templateCategory: PropTypes.string,
}
